import React, { Fragment } from "react";
import { toUSD } from "../../../util/currency-formatter";
import idx from "idx";
import { addSubTasksToOptions } from "src/routes/projects/utils/append-subtask";
import { getFormatedData } from "src/routes/projects/task-list/task";
import { roundValue, toDecimal } from "src/util/decimal-utils";

/**
 * @description
 * Renders a detailed breakdown of project stages, tasks, and subtasks with precise Decimal.js calculations.
 * Uses `toDecimal` and `roundValue` for financial accuracy to the cent.
 */
function DetailedVariationProposal({
  optionsData,
  projectTotal,
  isVariation,
  stageTotal,
  optionTotal,
  subTaskList,
  selectedOptionId = "",
  viewType = "full",
  stages,
}) {
  /**
   * @description
   * Process a subtask using Decimal.js for precise arithmetic.
   */
  const processSubTask = (subTask) => {
    const materialSale = toDecimal(subTask.materialTotal_SaleAmount);
    const laborSale = toDecimal(subTask.laborTotal_SaleAmount);
    const subcontractorSale = toDecimal(subTask.subcontractorTotal_SaleAmount);

    // total = material + labor + subcontractor
    const rawTotal = materialSale.plus(laborSale).plus(subcontractorSale);

    const qty = toDecimal(subTask.templateQuantity || 1);
    const unitPrice = qty.gt(0) ? rawTotal.div(qty) : rawTotal;

    const roundedUnitPrice = roundValue(unitPrice, 2);
    const roundedTotal = roundValue(rawTotal, 2);

    return {
      ...subTask,
      isSection: Boolean(subTask.is_section),
      parentSubtaskId:
        subTask.parentSubtaskId ?? null,
      order: Number(subTask.order || 0),
      qty: qty.toNumber(),
      unitPrice: roundedUnitPrice,
      unitPriceFormatted: toUSD(roundedUnitPrice),
      total: roundedTotal,
      totalFormatted: toUSD(roundedTotal),
    };
  };

  let updatedStageTotal = {};
  let updatedOptionTotal = {};

  /**
   * Stage and option preparation
   */
  if (!isVariation) {
    updatedStageTotal = stageTotal;
    updatedOptionTotal = addSubTasksToOptions(
      idx(optionTotal, (_) => _.stageSummary.projectlevel.optionList) || {},
      subTaskList
    );
  } else {
    if (Array.isArray(optionsData)) {
      optionsData.forEach((option) => {
        const finalSubtasks =
          option.subTasks ||
          subTaskList.filter(
            (st) => st.taskId === option.id || st.optionId === option.id
          );

        const processedSubtasks = finalSubtasks.map(processSubTask);
        updatedOptionTotal[option.id] = {
          ...option,
          subTasks: processedSubtasks,
        };
      });
    }
  }

  if (selectedOptionId && updatedOptionTotal[selectedOptionId]) {
    updatedOptionTotal = {
      [selectedOptionId]: updatedOptionTotal[selectedOptionId],
    };
  }

  /**
   * Flattens nested stages
   */
  const flattenStages = (stgs) => {
    const flat = [];
    const recurse = (s) => {
      const copy = { ...s };
      const children = copy.stages || [];
      delete copy.stages;
      flat.push(copy);
      children.forEach(recurse);
    };
    if (Array.isArray(stgs)) stgs.forEach(recurse);
    else if (stgs) recurse(stgs);
    return flat;
  };

  const flatStages = flattenStages(stages);

  /**
   * Rebuild tree hierarchy
   */
  const buildStageTree = (all, parentId = null) =>
    all
      .filter((s) => s.parent_stage_id === parentId)
      .map((s) => ({
        ...s,
        children: buildStageTree(all, s.id),
      }));

  /**
   * Renders a stage recursively
   */
  const renderStage = (stage, level = 0) => {
    const section = updatedStageTotal[stage.id] || stageTotal[stage.id];
    if (!section) return [];

    const stagesCost = getFormatedData(section);
    const isTop = level === 0;
    const stageClass = isTop ? "btn-bg-brand-3 text-white" : "";
    const stagePadding = isTop
      ? "ps-3"
      : `ps-${(level > 1 ? level + 1 : level) * 4}`;
    const stageTotalPadding =
      level <= 1 ? "pe-2" : `pe-${(level > 1 ? 1 + 2 : level + 2) * 7}`;

    let rows = [
      <tr key={stage.id} className="stage-row border-bottom-0">
        <td
          className={`text-left ${stagePadding} ${stageClass}`}
          data-cy="stage-name"
        >
          <b>{stage.name || section.stageHierarchy}</b>
        </td>
        <td className={stageClass}>
          <span
            className={`text-right ${stageTotalPadding}`}
            data-cy="stage-total"
          >
            {stagesCost.totalSaleAmount}
          </span>
        </td>
      </tr>,
    ];

    // Render children
    if (stage.children?.length) {
      stage.children.forEach((child) => {
        rows = rows.concat(renderStage(child, level + 1));
      });
    }

    // Render tasks
    if (section.taskList?.tasks && (isVariation || level === 0)) {
      Object.entries(section.taskList.tasks).forEach(
        ([taskId, task], index) => {
          const taskCost = getFormatedData(task);
          const taskPadding = isTop
            ? "ps-3"
            : `ps-${(level > 1 ? level + 1 : level) * 5}`;
          const taskTotalPadding = isTop
            ? "pe-2"
            : `pe-${(level > 1 ? 1 + 2 : level + 2) * 7}`;

          rows.push(
            <tr key={taskId} className={`${index === 0 ? "border-top-0" : ""}`}>
              <td className={`text-left ${taskPadding}`} data-cy="task-name">
                <b>{task.name || `Task ${taskId}`}</b>
              </td>
              <td>
                <span
                  className={`text-right ${taskTotalPadding}`}
                  data-cy="task-total"
                >
                  {taskCost.totalSaleAmount}
                </span>
              </td>
            </tr>
          );
        }
      );
    }

    return rows;
  };

  /**
   * Render variations / options and subtasks
   */
  const variationJSX = () => {
    if (!updatedOptionTotal || Object.keys(updatedOptionTotal).length === 0)
      return null;

    const buildSubTaskHierarchy = (subTasks = []) => {
      const buildNodes = (parentId = null) =>
        subTasks
          .filter(
            (subTask) => (subTask.parentSubtaskId || null) === (parentId || null)
          )
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((subTask) => ({
            ...subTask,
            children: subTask.isSection ? buildNodes(subTask.id) : [],
          }));

      return buildNodes();
    };

    const getSectionTotals = (sectionNode) => {
      const totals = sectionNode.children.reduce(
        (acc, child) => {
          if (child.isSection) {
            const childTotals = getSectionTotals(child);
            return {
              total: toDecimal(acc.total).plus(toDecimal(childTotals.total)),
            };
          }

          return {
            total: toDecimal(acc.total).plus(toDecimal(child.total || 0)),
          };
        },
        { total: 0 }
      );

      return totals;
    };

    const renderVariationRows = (nodes = [], depth = 0) =>
      nodes.flatMap((node) => {
        const paddingLeft = `${depth * 24 + 16}px`;

        if (node.isSection) {
          const sectionTotals = getSectionTotals(node);
          const rows = [
            <tr key={`section-${node.id}`}>
              <td
                className="text-left subtask-description-row fw-bold"
                style={{ paddingLeft }}
              >
                {node.description}
              </td>
              <td className="text-center"></td>
              <td className="text-right"></td>
              <td className="text-right fw-bold">
                {toUSD(sectionTotals.total)}
              </td>
            </tr>,
          ];

          return rows.concat(renderVariationRows(node.children, depth + 1));
        }

        return [
          <tr key={`subtask-${node.id}`}>
            <td
              className="text-left subtask-description-row"
              style={{ paddingLeft }}
            >
              {node.description}
            </td>
            <td className="text-center">{node.qty || 1}</td>
            <td className="text-right">{node.unitPriceFormatted}</td>
            <td className="text-right">{node.totalFormatted}</td>
          </tr>,
        ];
      });

    return Object.entries(updatedOptionTotal).map(
      ([optionId, option], index) => {
        if (optionId === "projectTotal") return null;
        const { name = "", revisionName = "", revisionNumber = 0 } = option;
        const optionCost = getFormatedData(option);
        const variationName =
          name +
          (revisionName && revisionNumber !== 1 ? ` - ${revisionName}` : "");

        const subTaskHierarchy = buildSubTaskHierarchy(option.subTasks || []);
        const subTaskContent = renderVariationRows(subTaskHierarchy);

        if ((viewType === "tasks_only" || viewType === "full") && isVariation) {
          return subTaskContent;
        }

        return (
          <Fragment key={index}>
            <tr>
              <td
                className={`text-left ${isVariation ? "" : "ps-3"}`}
                data-cy="variation-name"
              >
                {variationName}
              </td>
              {isVariation && <td></td>}
              <td>
                <span
                  className={`text-right ${isVariation ? "" : "pe-2"}`}
                  data-cy="variation-total"
                >
                  {isVariation
                    ? optionCost.totalSaleAmount
                    : optionTotal.stageSummary.projectlevel.totalSaleAmount}
                </span>
              </td>
            </tr>
            {isVariation && subTaskContent}
          </Fragment>
        );
      }
    );
  };

  const variationTotal = isVariation
    ? getFormatedData(idx(optionTotal, (_) => _.projectTotal) || {})
    : getFormatedData(idx(projectTotal, (_) => _) || {});

  const stageTree = buildStageTree(flatStages);

  return (
    <div className="col-12 my-3 table-responsive" data-cy="detailed-proposal">
      <table
        className={`table-create mb-0 table table-bordered fw-bolder ${isVariation ? "table-row-color" : ""
          }`}
      >
        <thead>
          <tr>
            <th
              className={`w-auto text-left summary-header-row ${isVariation ? "" : "ps-3"
                }`}
              data-cy="description"
            >
              Name
            </th>
            <th className="text-center summary-header-row">Qty</th>
            <th className="text-right summary-header-row">Unit Price</th>
            <th
              className={`text-right summary-header-row ${isVariation ? "" : "pe-5"
                }`}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {stageTree.map((stage) => renderStage(stage))}
          {isVariation && variationJSX()}
        </tbody>
      </table>

      <div className="d-flex flex-column align-items-end mt-4 mb-3">
        <div
          className="d-flex justify-content-between mb-2"
          style={{ width: "300px", fontSize: "0.9rem" }}
        >
          <span>Sub-Total:</span>
          <span className="fw-bold">{variationTotal.totalSaleAmount}</span>
        </div>
      </div>
    </div>
  );
}

export default DetailedVariationProposal;
